import React from 'react';
import { Box, Card, CardActionArea, CardContent, Grid, Typography } from '@mui/material';
import type { AboutValuesTemplate } from '../utils/corporateSectionSettings';

interface TemplateOption {
  id: AboutValuesTemplate;
  label: string;
  description: string;
  preview: React.ReactNode;
}

const TEMPLATE_OPTIONS: TemplateOption[] = [
  {
    id: 'cards',
    label: 'Kart Izgarası',
    description: 'Başlık üstte, değerler 3 sütunluk kartlarda',
    preview: (
      <Box sx={{ p: 1.25, height: '100%', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        <Box sx={{ height: 6, width: '42%', bgcolor: '#1e293b', borderRadius: 0.3, mx: 'auto' }} />
        <Box sx={{ height: 3, width: '58%', bgcolor: '#94a3b8', borderRadius: 0.3, mx: 'auto', mb: 0.3 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.45, flex: 1 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Box key={i} sx={{ bgcolor: '#fff', borderRadius: 0.5, boxShadow: '0 1px 3px rgba(15,23,42,0.08)', p: 0.4 }}>
              <Box sx={{ height: 8, width: 8, bgcolor: '#cbd5e1', borderRadius: 0.3, mb: 0.35 }} />
              <Box sx={{ height: 3, width: '80%', bgcolor: '#334155', borderRadius: 0.3, mb: 0.25 }} />
              <Box sx={{ height: 2, width: '100%', bgcolor: '#e2e8f0', borderRadius: 0.3 }} />
            </Box>
          ))}
        </Box>
      </Box>
    ),
  },
  {
    id: 'quality-assurance',
    label: 'Kalite Güvencesi',
    description: 'Solda metin ve madde listesi, sağda sertifika kartları',
    preview: (
      <Box sx={{ p: 1, height: '100%', bgcolor: '#fff', display: 'flex', gap: 0.7 }}>
        <Box sx={{ flex: 0.9, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.35 }}>
          <Box sx={{ height: 3, width: 28, bgcolor: '#b8956a', borderRadius: 0.3 }} />
          <Box sx={{ height: 7, width: '92%', bgcolor: '#1b4332', borderRadius: 0.3 }} />
          <Box sx={{ height: 3, width: '88%', bgcolor: '#94a3b8', borderRadius: 0.3 }} />
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
              <Box sx={{ height: 5, width: 5, borderRadius: '50%', border: '1px solid #1b4332' }} />
              <Box sx={{ height: 2.5, width: i === 1 ? '78%' : '88%', bgcolor: '#64748b', borderRadius: 0.3 }} />
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1.2, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.35 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Box
              key={i}
              sx={{
                bgcolor: '#f4f6f4',
                borderRadius: 0.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.25,
                p: 0.3,
              }}
            >
              <Box sx={{ height: 7, width: 7, bgcolor: '#d8e4dc', borderRadius: 0.4 }} />
              <Box sx={{ height: 2.5, width: '70%', bgcolor: '#1b4332', borderRadius: 0.3 }} />
              <Box sx={{ height: 2, width: '85%', bgcolor: '#cbd5e1', borderRadius: 0.3 }} />
            </Box>
          ))}
        </Box>
      </Box>
    ),
  },
  {
    id: 'text-columns',
    label: 'Metin Sütunları',
    description: 'Ortada başlık ve alt yazı, altta dikey çizgili metin sütunları',
    preview: (
      <Box sx={{ height: '100%', bgcolor: '#f4f7fb', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.4, px: 1 }}>
        <Box sx={{ height: 8, width: '46%', bgcolor: '#147a8c', borderRadius: 0.3 }} />
        <Box sx={{ height: 3, width: '34%', bgcolor: '#94a3b8', borderRadius: 0.3 }} />
        <Box sx={{ height: 3, width: '62%', bgcolor: '#cbd5e1', borderRadius: 0.3, mb: 0.4 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', width: '100%', flex: 1 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                px: 0.4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.3,
                borderLeft: i === 0 ? 'none' : '1px solid #e2e8f0',
              }}
            >
              <Box sx={{ height: 3, width: '70%', bgcolor: '#0f172a', borderRadius: 0.3 }} />
              <Box sx={{ height: 2, width: '90%', bgcolor: '#cbd5e1', borderRadius: 0.3 }} />
              <Box sx={{ height: 2, width: '80%', bgcolor: '#e2e8f0', borderRadius: 0.3 }} />
            </Box>
          ))}
        </Box>
      </Box>
    ),
  },
];

interface AboutValuesTemplatePickerProps {
  value: AboutValuesTemplate;
  onChange: (template: AboutValuesTemplate) => void;
}

const AboutValuesTemplatePicker: React.FC<AboutValuesTemplatePickerProps> = ({ value, onChange }) => (
  <Grid container spacing={2}>
    {TEMPLATE_OPTIONS.map((option) => {
      const selected = value === option.id;
      return (
        <Grid item xs={12} sm={4} key={option.id}>
          <Card
            variant={selected ? 'outlined' : 'elevation'}
            sx={{
              height: '100%',
              border: selected ? '2px solid' : '1px solid',
              borderColor: selected ? 'primary.main' : 'divider',
            }}
          >
            <CardActionArea onClick={() => onChange(option.id)} sx={{ height: '100%' }}>
              <Box sx={{ height: 112, borderBottom: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                {option.preview}
              </Box>
              <CardContent sx={{ py: 1.25, px: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold" fontSize="0.8rem">
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, display: 'block' }}>
                  {option.description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      );
    })}
  </Grid>
);

export default AboutValuesTemplatePicker;
